import { DeepMap } from 'react-hook-form';
import { SelectOptionElement } from '@odin-labs/components';
import { JobsiteUpdateInput } from 'apollo/generated/client-operations';
import { ensureNonUndefinedFields } from 'utils';
import { FormInputTypes, TypedFormInputs } from 'components/form';
import { getSchedulerOptions } from 'containers/jobsiteConfiguration/helpers/utils';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import { Jobsite, UserData } from './types';
import {
  getFeaturesSectionInputsHook,
  getFeaturesSectionDefaultValues,
  getFeaturesSectionUpdateInput,
} from './featuresSection';

export type EditJobsiteConfigTabFormData = Pick<
  EditJobsiteConfigurationFormData,
  'features'
>;

export type JobsiteSelectOptions = {
  schedulerOptions: SelectOptionElement[];
};

export const getDropdownsOptions = (args: {
  userData: UserData;
}): JobsiteSelectOptions => {
  const { userData } = args;
  const jobsiteUsers = userData?.users.edges.map(({ node }) => node) ?? [];

  return {
    schedulerOptions: getSchedulerOptions(jobsiteUsers),
  };
};

type GetFormInputsArgs = JobsiteSelectOptions & {
  jobsite: Jobsite;
};

export const getFormInputs = (
  args: GetFormInputsArgs,
): TypedFormInputs<EditJobsiteConfigTabFormData> => {
  const { jobsite, schedulerOptions } = args;

  const featuresSectionInputs = getFeaturesSectionInputsHook({
    jobsite,
    schedulerOptions,
  });

  return {
    features: {
      element: FormInputTypes.Section,
      layout: '',
      elementProps: {
        title: 'Features',
        details: 'Feature toggles',
        verticalGap: 'sm',
      },
      children: featuresSectionInputs,
    },
  };
};

type GetDefaultValuesArgs = {
  jobsite: Jobsite;
} & JobsiteSelectOptions;

export const getDefaultValues = (
  args: GetDefaultValuesArgs,
): EditJobsiteConfigTabFormData => {
  const { jobsite, schedulerOptions } = args;

  const features = getFeaturesSectionDefaultValues({
    jobsite,
    schedulerOptions,
  });

  return { features };
};

export const getJobsiteUpdateInput = (
  jobsite: Jobsite,
  data: EditJobsiteConfigTabFormData,
  dirtyFields: DeepMap<EditJobsiteConfigTabFormData, true>,
): JobsiteUpdateInput => {
  return ensureNonUndefinedFields<JobsiteUpdateInput>({
    jobsiteId: jobsite.jobsiteId,
    ...getFeaturesSectionUpdateInput(
      jobsite,
      data.features,
      dirtyFields.features,
    ),
  });
};
